import React, { Component } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { DateTime } from "luxon";
import DataBase from "../dataBase/liquidGuides";
import Guide from "../Models/Guide";
import Common from "../Common/Common";
import "./PollsSection.css";

// Simple guide-level polls (Recommendation #3c, the last piece of
// "collaboration basics" — conflict-safe saves and comments are both
// shipped). Guide-level only, unlike CommentsSection (which attaches to a
// specific place/event) — no targetType/item props, just one flat list per
// guide, same reasoning liquidGuides.js's addPoll/getPolls document. Mirrors
// CommentsSection's self-connect-to-Redux pattern and card-based layout.
class PollsSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      polls: null,
      creating: false,
      newQuestion: "",
      newOptions: ["", ""],
      posting: false,
      refreshing: false,
    };
  }

  componentDidMount() {
    this.loadPolls();
  }

  // No live listener — polls are a one-time fetch on mount (and after this
  // component's own create/vote/close/delete actions), so another person's
  // vote or new poll made while you're sitting on this screen wouldn't show
  // up until you left and came back. This gives a manual way to pull the
  // latest without that. `refreshing` is a separate flag from the initial
  // `polls === null` loading state specifically so a manual refresh doesn't
  // blank the existing list back to the "Loading…" placeholder while it's
  // in flight — it just re-spins the icon and swaps the list once the new
  // data arrives. Found 2026-10-03.
  loadPolls = (isRefresh) => {
    const { guide_info_id } = this.props;
    if (!guide_info_id) {
      this.setState({ polls: [] });
      return;
    }
    if (isRefresh) this.setState({ refreshing: true });
    DataBase.getPolls(guide_info_id, this.getPollsCallback);
  };

  getPollsCallback = (res) => {
    if (res.pass !== true) {
      this.setState({ refreshing: false });
      return;
    }
    this.setState({ polls: res.polls, refreshing: false });
  };

  // Matches the rules' poll-create check: owner or access granted by name.
  canCreatePoll = () => Guide.hasPersonalAccess(this.props.guide_info, this.props.user_info?.uid);

  canModerate = () => {
    const { guide_info, user_info } = this.props;
    if (!guide_info || !user_info) return false;
    if (guide_info.user_id === user_info.uid) return true;
    return Guide.hasPersonalModifyAccess(guide_info, user_info.uid);
  };

  startCreate = () => {
    this.setState({ creating: true, newQuestion: "", newOptions: ["", ""] });
  };

  cancelCreate = () => {
    this.setState({ creating: false });
  };

  setOptionText = (index, text) => {
    const newOptions = this.state.newOptions.slice();
    newOptions[index] = text;
    this.setState({ newOptions });
  };

  addOptionField = () => {
    this.setState({ newOptions: [...this.state.newOptions, ""] });
  };

  removeOptionField = (index) => {
    const newOptions = this.state.newOptions.slice();
    newOptions.splice(index, 1);
    this.setState({ newOptions });
  };

  handleCreatePoll = () => {
    const { guide_info_id, user_info } = this.props;
    const question = this.state.newQuestion.trim();
    const options = this.state.newOptions.map((t) => t.trim()).filter((t) => t !== "");
    if (question === "" || options.length < 2 || !guide_info_id || user_info.uid === "guest") return;
    const name = `${user_info.first_name || ""} ${user_info.last_name || ""}`.trim();
    const poll = {
      question,
      options: options.map((text, i) => ({ id: String(i), text })),
      created_by: user_info.uid,
      created_by_name: name || user_info.email,
      date: DateTime.utc().toISO(),
    };
    this.setState({ posting: true });
    DataBase.addPoll(guide_info_id, poll, (res) => {
      this.setState({ posting: false });
      if (res.pass !== true) return;
      this.setState({ creating: false });
      this.loadPolls();
    });
  };

  handleVote = (poll, optionId) => {
    const { guide_info_id, user_info } = this.props;
    if (user_info.uid === "guest" || poll.data.closed) return;
    const myCurrentVote = poll.data.votes ? poll.data.votes[user_info.uid] : undefined;
    // Clicking the option already voted for withdraws the vote (toggle),
    // rather than forcing exactly one click to change votes and a
    // different action to clear one — matches the single-click-to-toggle
    // convention the "Include maps"/Photo Book checkboxes already use
    // elsewhere in this app.
    if (myCurrentVote === optionId) {
      DataBase.removeVote(guide_info_id, poll.id, user_info.uid, (res) => {
        if (res.pass === true) this.loadPolls();
      });
    } else {
      DataBase.votePoll(guide_info_id, poll.id, user_info.uid, optionId, (res) => {
        if (res.pass === true) this.loadPolls();
      });
    }
  };

  handleToggleClosed = (poll) => {
    const { guide_info_id } = this.props;
    DataBase.setPollClosed(guide_info_id, poll.id, !poll.data.closed, (res) => {
      if (res.pass === true) this.loadPolls();
    });
  };

  handleDelete = (poll_id) => {
    const { guide_info_id } = this.props;
    DataBase.deletePoll(guide_info_id, poll_id, (res) => {
      if (res.pass === true) this.loadPolls();
    });
  };

  renderPoll = (poll) => {
    const { user_info } = this.props;
    const canModerate = this.canModerate();
    const isCreator = poll.data.created_by === user_info.uid;
    const votes = poll.data.votes || {};
    const totalVotes = Object.keys(votes).length;
    const myVote = votes[user_info.uid];
    return (
      <div className="ps-poll" key={poll.id}>
        <div className="ps-poll-head">
          <span className="ps-poll-question">{poll.data.question}</span>
          {isCreator || canModerate ? (
            <div className="ps-poll-actions">
              <FontAwesomeIcon
                // solid() is a Babel macro — needs a literal string at
                // compile time, not an expression. Resolving the ternary
                // OUTSIDE the call (two separate literal calls) instead of
                // inside it, which build failed on. Found 2026-10-03.
                icon={poll.data.closed ? solid("lock-open") : solid("lock")}
                className="ps-poll-action-icon"
                title={poll.data.closed ? "Re-open voting" : "Close voting"}
                onClick={() => this.handleToggleClosed(poll)}
              />
              <FontAwesomeIcon
                icon={solid("trash")}
                className="ps-poll-action-icon"
                title="Delete poll"
                onClick={() => this.handleDelete(poll.id)}
              />
            </div>
          ) : null}
        </div>
        <div className="ps-poll-meta">
          by {poll.data.created_by_name} · {Common.formatDateLuxon(poll.data.date)}
          {poll.data.closed ? <span className="ps-poll-closed-badge"> · Closed</span> : null}
        </div>
        <div className="ps-options">
          {poll.data.options.map((option) => {
            const count = Object.values(votes).filter((v) => v === option.id).length;
            const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
            const mine = myVote === option.id;
            return (
              <div
                className={`ps-option${mine ? " ps-option-mine" : ""}${poll.data.closed ? " ps-option-disabled" : ""}`}
                key={option.id}
                onClick={() => !poll.data.closed && this.handleVote(poll, option.id)}
              >
                <div className="ps-option-bar" style={{ width: `${pct}%` }} />
                <div className="ps-option-row">
                  <span className="ps-option-text">
                    {mine ? <FontAwesomeIcon icon={solid("check")} className="ps-option-check" /> : null}
                    {option.text}
                  </span>
                  <span className="ps-option-count">
                    {count} {count === 1 ? "vote" : "votes"} ({pct}%)
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  render() {
    const { polls, creating } = this.state;
    const { user_info } = this.props;
    return (
      <>
        <div className="ps-section-label-row">
          <span className="ps-section-label">Polls</span>
          <FontAwesomeIcon
            icon={solid("arrows-rotate")}
            className={`ps-refresh-icon${this.state.refreshing ? " ps-refresh-spinning" : ""}`}
            title="Refresh"
            onClick={() => this.loadPolls(true)}
          />
        </div>
        <div className="ps-card">
          {polls == null ? (
            <div className="ps-empty">Loading…</div>
          ) : polls.length === 0 && !creating ? (
            <div className="ps-empty">No polls yet.</div>
          ) : (
            polls.map((poll) => this.renderPoll(poll))
          )}
          {creating ? (
            <div className="ps-create">
              <input
                className="ps-create-question"
                placeholder="Ask a question…"
                value={this.state.newQuestion}
                onChange={(e) => this.setState({ newQuestion: e.target.value })}
              />
              {this.state.newOptions.map((text, i) => (
                <div className="ps-create-option-row" key={i}>
                  <input
                    className="ps-create-option"
                    placeholder={`Option ${i + 1}`}
                    value={text}
                    onChange={(e) => this.setOptionText(i, e.target.value)}
                  />
                  {this.state.newOptions.length > 2 ? (
                    <FontAwesomeIcon
                      icon={solid("xmark")}
                      className="ps-create-option-remove"
                      onClick={() => this.removeOptionField(i)}
                    />
                  ) : null}
                </div>
              ))}
              <button className="ps-create-add-option" onClick={this.addOptionField}>
                + Add option
              </button>
              <div className="ps-create-buttons">
                <button
                  className="ps-create-submit"
                  disabled={
                    this.state.newQuestion.trim() === "" ||
                    this.state.newOptions.map((t) => t.trim()).filter((t) => t !== "").length < 2 ||
                    this.state.posting
                  }
                  onClick={this.handleCreatePoll}
                >
                  Create Poll
                </button>
                <button className="ps-create-cancel" onClick={this.cancelCreate}>
                  Cancel
                </button>
              </div>
            </div>
          ) : this.canCreatePoll() ? (
            <button className="ps-new-poll-button" onClick={this.startCreate}>
              + New Poll
            </button>
          ) : null}
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  guide_info_id: state.guide_meta.guide_info_id,
  guide_info: state.guide_meta.guide_info,
  user_info: state.user_info,
});

export default connect(mapStateToProps)(PollsSection);
